import { type JSX } from "react";
import { useTranslation } from "react-i18next";

import { DxfPreview } from "@/components/DxfPreview";
import { Pill } from "@/components/ui/Pill";

interface Props {
  fileName: string;
  /** The original upload's preview; ``null`` = no preview, only the repaired side renders. */
  originalPreviewUrl: string | null;
  repairedPreviewUrl: string;
}

/**
 * The before / after pair of the repair compare — the original file (left)
 * and the repaired manufacturing copy (right). Shared by ``RepairCompare``
 * and the stopped quote's review step (KAN-2081), so the customer sees the
 * same comparison before they switch to the repaired copy as after.
 */
export function RepairBeforeAfter({
  fileName,
  originalPreviewUrl,
  repairedPreviewUrl,
}: Props): JSX.Element {
  const { t } = useTranslation("quote");
  return (
    <div className="grid gap-3 p-3 sm:grid-cols-2">
      {originalPreviewUrl && (
        <div className="flex flex-col gap-1.5">
          <span className="rounded border border-border-strong bg-surface px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-ink-3 self-start">
            {t("repair.beforeLabel")}
          </span>
          <DxfPreview
            src={originalPreviewUrl}
            alt={`${fileName} — ${t("repair.beforeLabel")}`}
            chrome={false}
            className="aspect-[4/3] rounded border border-border"
          />
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Pill tone="ok">{t("repair.afterLabel")}</Pill>
        <DxfPreview
          src={repairedPreviewUrl}
          alt={`${fileName} — ${t("repair.afterLabel")}`}
          chrome={false}
          className="aspect-[4/3] rounded border border-border"
        />
      </div>
    </div>
  );
}
